import { api } from '../../lib/apiClient';

export type WalletListItem = {
  id: string;
  address: string;
  user_identity_id: string;
  email?: string;
  provider_user_id: string;
  auth_provider: string;
  chain_type: string;
  curve: string;
  networks?: string[];
  status: string;
  signing_mode: 'client' | 'tee';
  created_at: string;
  user_last_seen_at: string;
};

export type SmartAccountListItem = {
  id: string;
  wallet_id: string;
  owner_address: string;
  chain: string;
  factory: string;
  address: string;
  deployed: boolean;
  sponsor_policy?: string;
  created_by: 'user' | 'owner';
  created_at: string;
};

export type WalletsPage = {
  items: WalletListItem[];
  total: number;
  limit: number;
  offset: number;
};

export type WalletsSummary = {
  wallet_count: number;
  distinct_assets: number;
  total_value: number;
  quote: 'EUR' | 'USD';
};

export type AssetView = {
  chain?: string;
  symbol: string;
  name: string;
  contract?: string;
  balance: string;
  price: number;
  value: number;
  change_24h_pct: number;
  quote: 'EUR' | 'USD';
  coingecko_id?: string;
};

export type WalletAssetsView = {
  wallet_id: string;
  quote: 'EUR' | 'USD';
  total_value: number;
  assets: AssetView[];
  stale_at: string;
};

export type WalletDetail = {
  id: string;
  address: string;
  user_identity_id: string;
  chain_type: string;
  curve: string;
  networks?: string[];
  status: 'active' | 'deprecated' | 'archived';
  created_at: string;
  account_type: 'eoa' | 'smart';
  signer_address?: string;
  smart_account?: string;
  /** When status === 'deprecated', the active wallet for this user (if any). */
  replacement_wallet_id?: string;
};

export async function listWallets(
  orgId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<WalletsPage> {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const res = await api.get<{ data: WalletsPage }>(
    `/v1/orgs/${orgId}/wallets?limit=${limit}&offset=${offset}`,
  );
  return res.data.data;
}

export async function getWalletsSummary(orgId: string): Promise<WalletsSummary> {
  const res = await api.get<{ data: WalletsSummary }>(`/v1/orgs/${orgId}/wallets/summary`);
  return res.data.data;
}

export async function getWalletAssets(orgId: string, walletId: string): Promise<WalletAssetsView> {
  const res = await api.get<{ data: WalletAssetsView }>(
    `/v1/orgs/${orgId}/wallets/${walletId}/assets`,
  );
  return res.data.data;
}

export async function getWalletDetail(orgId: string, walletId: string): Promise<WalletDetail> {
  const res = await api.get<{ data: WalletDetail }>(
    `/v1/orgs/${orgId}/wallets/${walletId}`,
  );
  return res.data.data;
}

export async function hideToken(orgId: string, chain: string, contract: string, note?: string): Promise<void> {
  await api.post(`/v1/orgs/${orgId}/token-blacklist`, { chain, contract, note });
}

export async function unhideToken(orgId: string, chain: string, contract: string): Promise<void> {
  await api.delete(`/v1/orgs/${orgId}/token-blacklist/${chain}/${contract}`);
}

export async function listOrgSmartAccounts(orgId: string): Promise<SmartAccountListItem[]> {
  const res = await api.get<{ data: { items: SmartAccountListItem[] } }>(
    `/v1/orgs/${orgId}/smart-accounts`,
  );
  return res.data.data.items;
}
